import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, ReferenceLine } from 'recharts';
import { cn } from '../../../utils/classnames';
import { formatCurrency } from '../../../utils/formatters';

function getPaddedDomain(minValue, maxValue, paddingPct = 0.12) {
  if (minValue === 0 && maxValue === 0) return [-1, 1];

  const absMax = Math.max(Math.abs(minValue), Math.abs(maxValue));
  const pad = absMax * paddingPct;

  const yMin = minValue < 0 ? Math.floor(minValue - pad) : 0;
  const yMax = maxValue > 0 ? Math.ceil(maxValue + pad) : 0;

  if (yMax === 0 && yMin < 0) return [yMin, Math.ceil(pad) || 1];
  if (yMin === 0 && yMax > 0) return [0, yMax];

  return [yMin, yMax];
}

const labelStyle = {
  fontSize: 12,
  fontWeight: 400,
  fill: '#143145',
  fontFamily: 'Jost, sans-serif',
};

const NegativeRoundedBottomShape = (props) => {
  const { x, y, width, height, fill } = props;

  if (!width || !height) return null;

  const top = Math.min(y, y + height);
  const bottom = Math.max(y, y + height);
  const h = bottom - top;

  const left = x;
  const right = x + width;

  const r = 12;
  const rr = Math.min(r, width / 2, h / 2);

  const d = `
    M ${left} ${top}
    L ${right} ${top}
    L ${right} ${bottom - rr}
    Q ${right} ${bottom} ${right - rr} ${bottom}
    L ${left + rr} ${bottom}
    Q ${left} ${bottom} ${left} ${bottom - rr}
    Z
  `;

  return <path d={d} fill={fill} />;
};

const NegativeValueLabel = ({ x, y, width, height, value }) => {
  const v = Number(value || 0);
  if (!Number.isFinite(v) || v >= 0) return null;

  const top = Math.min(y, y + height);
  const bottom = Math.max(y, y + height);

  const cx = x + width / 2;
  const textY = bottom + 16;

  return (
    <text x={cx} y={textY} textAnchor="middle" {...labelStyle}>
      {formatCurrency(v)}
    </text>
  );
};

const SingleValueTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  const pos = payload.find((p) => p && p.dataKey === 'posValue');
  const neg = payload.find((p) => p && p.dataKey === 'negValue');

  const rawValue =
    (pos && Number(pos.value) !== 0 ? Number(pos.value) : null) ??
    (neg && Number(neg.value) !== 0 ? Number(neg.value) : null) ??
    0;

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E6EEF5',
        borderRadius: 10,
        padding: '10px 12px',
        boxShadow: '0 6px 18px rgba(15, 46, 73, 0.12)',
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, color: '#143145', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 12, color: '#143145' }}>
        <span style={{ fontWeight: 600 }}>Valor:</span> {formatCurrency(rawValue)}
      </div>
    </div>
  );
};

const BarRoundedChart = ({
  data,
  valueKey = 'value',
  labelKey = 'label',
  barColor = '#2D86B4',
  negativeBarColor = '#B76955',
  className = '',
}) => {
  const preparedData = useMemo(() => {
    return (data || []).map((d) => {
      const v = Number(d[valueKey] || 0);
      return {
        ...d,
        posValue: v > 0 ? v : 0,
        negValue: v < 0 ? v : 0,
      };
    });
  }, [data, valueKey]);

  const { domain, hasNegative, hasPositive } = useMemo(() => {
    const values = (data || []).map((d) => Number(d[valueKey] || 0));
    const minV = values.length ? Math.min(...values) : 0;
    const maxV = values.length ? Math.max(...values) : 0;

    return {
      domain: getPaddedDomain(minV, maxV, 0.12),
      hasNegative: minV < 0,
      hasPositive: maxV > 0,
    };
  }, [data, valueKey]);

  return (
    <div className={cn('w-full', className)}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={preparedData} margin={{ top: 22, right: 20, left: 0, bottom: 10 }}>
          <XAxis
            dataKey={labelKey}
            axisLine={false}
            tickLine={false}
            tickMargin={16}
            tick={{ fontSize: 12, fontWeight: 600, fill: '#143145' }}
          />

          <YAxis hide domain={domain} />

          {(hasNegative || hasPositive) && <ReferenceLine y={0} stroke="#D6DEE6" strokeWidth={1} />}

          <Tooltip content={<SingleValueTooltip />} cursor={{ fill: 'transparent' }} />

          <Bar dataKey="posValue" fill={barColor} stackId="v" radius={[12, 12, 0, 0]} maxBarSize={50}>
            <LabelList
              dataKey="posValue"
              position="top"
              offset={12}
              formatter={(v) => formatCurrency(v)}
              style={labelStyle}
            />
          </Bar>
          <Bar
            dataKey="negValue"
            fill={negativeBarColor}
            stackId="v"
            maxBarSize={50}
            shape={<NegativeRoundedBottomShape />}
          >
            <LabelList dataKey="negValue" content={<NegativeValueLabel />} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BarRoundedChart;
